import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { GrossReceiptsStatusViewStore } from "../../store";
import { GrossReceiptsStatusView } from "./GrossReceiptsStatusView";
import { GrossReceiptsStatusUpdateStore } from "../../store";
import { GrossReceiptsStatusUpdate } from "../update/GrossReceiptsStatusUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type GrossReceiptsStatusDrawerContentProps = {
  entityId: string;
  mode?: "view" | "update";
};

export const GrossReceiptsStatusDrawerContent: React.FC<GrossReceiptsStatusDrawerContentProps> =
  observer(({ entityId, mode = "view" }) => {
    const rootStore = useRootStore();
    const viewInitializedRef = useRef<string | null>(null);
    const updateInitializedRef = useRef<string | null>(null);

    const viewStore = useMemo(() => {
      const s = new GrossReceiptsStatusViewStore(rootStore);
      s.isDrawerMode = true;
      return s;
    }, [rootStore]);

    useEffect(() => {
      if (
        mode === "view" &&
        entityId &&
        viewInitializedRef.current !== entityId
      ) {
        viewInitializedRef.current = entityId;
        viewStore.initialize({ id: entityId });
      }
    }, [entityId, mode, viewStore]);

    const updateStore = useMemo(() => {
      const s = new GrossReceiptsStatusUpdateStore(rootStore);
      s.isDrawerMode = true;
      return s;
    }, [rootStore]);

    useEffect(() => {
      if (
        mode === "update" &&
        entityId &&
        updateInitializedRef.current !== entityId
      ) {
        updateInitializedRef.current = entityId;
        updateStore.initialize({ id: entityId });
      }
    }, [entityId, mode, updateStore]);

    if (mode === "update") {
      if (
        updateStore.status === "processing" ||
        updateStore.status === "uninitialized"
      ) {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <CircularProgress />
          </Box>
        );
      }
      return (
        <GrossReceiptsStatusUpdate
          grossReceiptsStatusUpdateStore={updateStore}
        />
      );
    }

    if (
      viewStore.status === "processing" ||
      viewStore.status === "uninitialized"
    ) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return <GrossReceiptsStatusView grossReceiptsStatusViewStore={viewStore} />;
  });
